import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

import { UserContext } from './hooks/UserContext';
import Header from './components/Header'
import Loading from './components/Loading'

// Editor for the global search synonym map ("ww2" -> "world war ii").
// The app applies these query-side, so changes take effect on the next
// catalog sync — no app release needed.
export const ManageSynonyms = () => {
    const [rows, setRows] = useState([]); // [{key, expansions}] expansions as comma-string
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const { user } = useContext(UserContext);

    const authHeaders = useCallback(() => ({
        headers: { 'Authorization': `Bearer ${user.token}`, 'X-EDITOR': 'nanoms' }
    }), [user.token]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const res = await axios.get('/api/search/synonyms', authHeaders());
                const map = res.data.synonyms || {};
                const loaded = Object.keys(map).sort().map(key => ({
                    key,
                    expansions: map[key].join(', '),
                }));
                setRows(loaded);
            } catch (error) {
                console.error(error);
                setMessage('Failed to load synonyms.');
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [authHeaders]);

    const updateRow = (index, field, value) => {
        setRows(rows.map((row, i) => i === index ? { ...row, [field]: value } : row));
    };

    const addRow = () => setRows([...rows, { key: '', expansions: '' }]);

    const removeRow = (index) => setRows(rows.filter((_, i) => i !== index));

    const saveAll = async () => {
        setSaving(true);
        setMessage('');
        const synonyms = {};
        for (const row of rows) {
            const key = row.key.trim().toLowerCase();
            const expansions = row.expansions
                .split(',')
                .map(s => s.trim().toLowerCase())
                .filter(Boolean);
            if (key && expansions.length) synonyms[key] = expansions;
        }
        try {
            const res = await axios.put('/api/search/synonyms', { synonyms }, authHeaders());
            const map = res.data.synonyms || {};
            setRows(Object.keys(map).sort().map(key => ({
                key,
                expansions: map[key].join(', '),
            })));
            setMessage(`Saved ${Object.keys(map).length} entries.`);
        } catch (error) {
            console.error(error);
            setMessage('Save failed, please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (<div><Header /><Loading /></div>);

    return (
        <div>
            <Header />
            <div style={{ maxWidth: 720, margin: '24px auto', padding: '0 16px' }}>
                <h4>Search synonyms</h4>
                <p style={{ color: '#666', fontSize: 14 }}>
                    When a user types the term on the left, search also tries the
                    expansions on the right (comma-separated). Keep entries lowercase
                    and obviously correct — they apply to every search in the app.
                </p>
                {rows.map((row, index) => (
                    <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <InputText
                            value={row.key}
                            onChange={e => updateRow(index, 'key', e.target.value)}
                            placeholder="term, e.g. ww2"
                            style={{ width: 180 }}
                        />
                        <InputText
                            value={row.expansions}
                            onChange={e => updateRow(index, 'expansions', e.target.value)}
                            placeholder="expansions, e.g. world war ii, world war"
                            style={{ flex: 1 }}
                        />
                        <Button
                            icon="pi pi-trash"
                            className="p-button-text p-button-danger"
                            onClick={() => removeRow(index)}
                        />
                    </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <Button label="Add entry" icon="pi pi-plus" className="p-button-outlined" onClick={addRow} />
                    <Button label={saving ? 'Saving…' : 'Save all'} icon="pi pi-save" disabled={saving} onClick={saveAll} />
                    <span style={{ alignSelf: 'center', color: '#666' }}>{message}</span>
                </div>
            </div>
        </div>
    );
}
